import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  deleteReel,
  getEntitlement,
  getReels,
  getVods,
  isTwitchSignInRequiredError,
  renameReel,
} from '../api/rttsClient';
import type { Entitlement, ReelDurationTier, ReelSummary, Vod } from '../api/types';
import { beginTwitchConnect } from '../auth/beginTwitchConnect';
import { setMockAuthState } from '../api/mockState';
import {
  STUDIO_COPY,
  isStudioJobInProgress,
  isStudioUseMock,
} from '../config';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { formatDuration, formatVodDate } from '../utils/format';
import { isReelNameValid, normalizeReelName } from '../utils/reelName';
import {
  nextVodEmptyKind,
  pickNextStudioVod,
  recentReels,
} from '../utils/studioHome';
import { formatVodsEntitlementIndicator } from '../utils/vodEligibility';
import ReelCard from './ReelCard';
import ReelNameField from './ReelNameField';
import DurationTierPicker from './DurationTierPicker';
import ReconnectTwitchCard from './ReconnectTwitchCard';
import StudioExpectationPanel from './StudioExpectationPanel';
import StudioProgressPanel from './StudioProgressPanel';
import StudioToast from './StudioToast';
import StudioVodExpiry from './StudioVodExpiry';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; vods: Vod[]; reels: ReelSummary[]; entitlement: Entitlement | null }
  | { kind: 'reconnect' }
  | { kind: 'error'; message: string };

const StudioHomeDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { status, setMockState, refreshMock } = useStudioAuth();
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' });
  const [toast, setToast] = useState<string | null>(null);
  const [reelName, setReelName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [durationTier, setDurationTier] = useState<ReelDurationTier>('mid');

  const load = useCallback(async () => {
    setLoadState({ kind: 'loading' });
    try {
      const [vods, reelsResult, entitlement] = await Promise.all([
        getVods(),
        getReels().catch(() => ({ reels: [] as ReelSummary[] })),
        getEntitlement().catch(() => null),
      ]);
      setLoadState({
        kind: 'ready',
        vods,
        reels: reelsResult.reels,
        entitlement,
      });
    } catch (error) {
      if (isTwitchSignInRequiredError(error)) {
        if (isStudioUseMock()) {
          setMockAuthState('stale_link');
          refreshMock();
        }
        setLoadState({ kind: 'reconnect' });
        return;
      }
      setLoadState({
        kind: 'error',
        message: 'Could not load your studio. Please try again.',
      });
    }
  }, [refreshMock]);

  useEffect(() => {
    if (status === 'stale_link') {
      setLoadState({ kind: 'reconnect' });
      return;
    }
    void load();
  }, [status, load]);

  const next = useMemo(() => {
    if (loadState.kind !== 'ready') return null;
    return pickNextStudioVod(loadState.vods, loadState.reels);
  }, [loadState]);

  const emptyKind = useMemo(() => {
    if (loadState.kind !== 'ready') return null;
    return nextVodEmptyKind(loadState.vods, loadState.reels);
  }, [loadState]);

  const recent = useMemo(() => {
    if (loadState.kind !== 'ready') return [];
    return recentReels(loadState.reels, 3);
  }, [loadState]);

  const allotment = useMemo(() => {
    if (loadState.kind !== 'ready' || !loadState.entitlement) return null;
    return formatVodsEntitlementIndicator(loadState.entitlement);
  }, [loadState]);

  const handleDelete = async (jobId: string) => {
    if (loadState.kind !== 'ready') return;
    const snapshot = loadState.reels;
    setLoadState({
      ...loadState,
      reels: snapshot.filter((row) => row.job_id !== jobId),
    });
    try {
      await deleteReel(jobId);
    } catch {
      setLoadState({ ...loadState, reels: snapshot });
      throw new Error(STUDIO_COPY.reelsDeleteError);
    }
  };

  const handleRename = async (jobId: string, reelName: string) => {
    if (loadState.kind !== 'ready') return;
    const snapshot = loadState;
    setLoadState({
      ...loadState,
      reels: loadState.reels.map((row) =>
        row.job_id === jobId
          ? { ...row, reel_name: reelName, needs_reel_name: false }
          : row
      ),
    });
    try {
      const updated = await renameReel(jobId, reelName);
      setLoadState((prev) => {
        if (prev.kind !== 'ready') return prev;
        return {
          ...prev,
          reels: prev.reels.map((row) =>
            row.job_id === jobId ? { ...row, ...updated } : row
          ),
        };
      });
    } catch (err) {
      setLoadState(snapshot);
      throw err;
    }
  };

  if (loadState.kind === 'loading') {
    return (
      <div className="flex justify-center py-16" data-testid="studio-home-loading">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (loadState.kind === 'reconnect') {
    return (
      <div data-testid="studio-home-reconnect">
        <ReconnectTwitchCard
          onReconnect={() => {
            const shouldNavigate = beginTwitchConnect({ returnTo: '/studio' });
            if (shouldNavigate) {
              setMockState('linked');
              void load();
            }
          }}
        />
      </div>
    );
  }

  if (loadState.kind === 'error') {
    return (
      <div className="studio-panel space-y-3" role="alert">
        <p>{loadState.message}</p>
        <button type="button" className="studio-btn-ghost" onClick={() => void load()}>
          {STUDIO_COPY.tryAgain}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="studio-home">
      <h1 className="studio-page-heading">{STUDIO_COPY.studioPageHeading}</h1>

      {emptyKind === 'none' ? (
        <div
          className="studio-home-empty"
          data-testid="studio-home-next-vod"
          data-empty="none"
        >
          <p className="studio-card-title">{STUDIO_COPY.homeNoVodsHeadline}</p>
          <Link
            to="/studio/connect"
            className="studio-btn-primary no-underline"
          >
            {STUDIO_COPY.homeNoVodsCta}
          </Link>
        </div>
      ) : null}

      {emptyKind === 'too_short' ? (
        <div
          className="studio-home-empty"
          data-testid="studio-home-next-vod"
          data-empty="too_short"
        >
          <p className="studio-card-title">{STUDIO_COPY.vodsAllTooShortHeadline}</p>
          <p className="text-[var(--studio-muted)]">{STUDIO_COPY.vodsAllTooShortBody}</p>
        </div>
      ) : null}

      {emptyKind === 'caught_up' ? (
        <div
          className="studio-home-empty"
          data-testid="studio-home-next-vod"
          data-empty="caught_up"
        >
          <p className="studio-card-title">{STUDIO_COPY.homeCaughtUp}</p>
        </div>
      ) : null}

      {next && emptyKind === null ? (
        <article className="studio-next-vod" data-testid="studio-home-next-vod">
          <div className="studio-next-vod-left">
            <div className="studio-next-vod-status">
              <span
                className="studio-status-pill"
                data-testid="studio-home-next-status"
              >
                {next.runningReel
                  ? STUDIO_COPY.reelsCuttingBadge
                  : STUDIO_COPY.statusReady}
              </span>
              <p className="studio-card-body">{STUDIO_COPY.homeNextVodKicker}</p>
            </div>
            <div className="studio-next-vod-media">
              <div className="studio-next-vod-thumb">
                <img src={next.vod.thumbnail_url} alt="" />
                <span className="studio-next-vod-duration">
                  {formatDuration(next.vod.duration_sec)}
                </span>
              </div>
              <div className="space-y-1 min-w-0">
                <p className="studio-card-title">{next.vod.title}</p>
                <p className="studio-card-body text-[var(--studio-muted)]">
                  {formatVodDate(next.vod.created_at)} · {STUDIO_COPY.homeProviderTwitch}
                </p>
                <StudioVodExpiry vod={next.vod} />
              </div>
            </div>
            {next.runningReel ? (
              <Link
                to={`/studio/reel/${encodeURIComponent(next.runningReel.job_id)}`}
                className="studio-btn-ghost no-underline"
              >
                {STUDIO_COPY.reelsLeaveCta}
              </Link>
            ) : (
              <div className="studio-next-vod-name">
                <ReelNameField
                  value={reelName}
                  onChange={setReelName}
                  showError={nameTouched}
                />
                {loadState.kind === 'ready' &&
                loadState.entitlement?.duration_tiers_enabled ? (
                  <DurationTierPicker
                    value={durationTier}
                    onChange={setDurationTier}
                  />
                ) : null}
                <button
                  type="button"
                  className="studio-btn-primary"
                  data-testid="studio-home-cut-cta"
                  disabled={!isReelNameValid(reelName)}
                  onClick={() => {
                    setNameTouched(true);
                    const name = normalizeReelName(reelName);
                    if (!isReelNameValid(name)) return;
                    navigate(
                      `/studio/generate/${encodeURIComponent(next.vod.vod_id)}`,
                      { state: { reelName: name, durationTier } }
                    );
                  }}
                >
                  {STUDIO_COPY.homeNextVodCta}
                </button>
              </div>
            )}
          </div>
          <div className="studio-next-vod-right">
            {next.runningReel && isStudioJobInProgress(next.runningReel.status) ? (
              <StudioProgressPanel
                status={next.runningReel.status}
                vodDurationSec={next.vod.duration_sec}
                allowLeave
              />
            ) : (
              <StudioExpectationPanel />
            )}
          </div>
        </article>
      ) : null}

      {recent.length > 0 ? (
        <section className="space-y-3" data-testid="studio-home-recent">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="studio-page-heading">{STUDIO_COPY.homeRecentHeading}</h2>
            <Link
              to="/studio/reels"
              className="studio-fine-print no-underline hover:text-[var(--studio-accent-bright)]"
            >
              {STUDIO_COPY.homeViewAll}
            </Link>
          </div>
          <div className="studio-home-recent-strip">
            {recent.map((row) => (
              <ReelCard
                key={row.job_id}
                reel={row}
                onDelete={handleDelete}
                onRename={handleRename}
                onToast={setToast}
              />
            ))}
          </div>
        </section>
      ) : null}

      {allotment ? (
        <div className="studio-home-allotment" data-testid="studio-home-allotment">
          <span>{allotment.text}</span>
          {allotment.showUpgrade ? (
            <Link
              to="/studio/upgrade"
              className="studio-btn-ghost no-underline text-sm px-3 py-1"
            >
              {STUDIO_COPY.upgradeCta}
            </Link>
          ) : null}
        </div>
      ) : null}

      <StudioToast message={toast} onDismiss={() => setToast(null)} />
    </div>
  );
};

export default StudioHomeDashboard;
